import React, { useRef, useEffect, useCallback } from 'react';
import BordTooltip from 'components/BordTooltip/BordTooltip';
import { COLUMN_TYPE, ITrelloColumn } from './TrelloColumnTypes';
import { BordOneToneIcon } from 'components/NudosComponents';
import LoaderTrelloCard from 'views/shopping/components/LoaderTrelloCard/LoaderTrelloCard';
import './TrelloColumn.scss';

/**
 * Componente de columna para boards tipo Trello.
 * Utilizado para mostrar columnas en el sistema de tracking de órdenes y servicios logísticos.
 */
const TrelloColumn = ({
  title,
  subtitle,
  variantIcon,
  items,
  isLoading,
  renderItem,
  columnType = COLUMN_TYPE.DEFAULT,
  onLoadMore,
  isLoadingMoreItems,
  hasMoreItems,
  trelloColumnHeaderCustomClassName
}: ITrelloColumn) => {
  const columnContentRef = useRef<HTMLDivElement>(null);
  const itemsCount = items?.length || 0;
  const displayCount = itemsCount > 9 ? '9+' : itemsCount;
  const shouldShowTooltip = itemsCount > 9;
  const counterContent = <div className={`counterItemsColumnHeader ${columnType}`}>{displayCount}</div>;

  const handleScroll = useCallback(() => {
    if (!columnContentRef.current || !onLoadMore || !hasMoreItems || isLoadingMoreItems) return;
    const { scrollTop, scrollHeight, clientHeight } = columnContentRef.current;
    const threshold = 100;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < threshold;
    if (isNearBottom) {
      onLoadMore();
    }
  }, [onLoadMore, hasMoreItems, isLoadingMoreItems]);

  const setupScrollListeners = useCallback(() => {
    const contentElement = columnContentRef.current;
    if (!contentElement) return;

    contentElement.addEventListener('scroll', handleScroll);
    return () => {
      contentElement.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    return setupScrollListeners();
  }, [setupScrollListeners]);

  return (
    <div className="trelloColumn">
      <div className={`trelloColumnHeader ${trelloColumnHeaderCustomClassName}`}>
        <div className="trelloColumnHeaderLeft">
          <div className={`lineColumnHeader ${columnType}`}></div>
          <div className={`iconColumnHeader ${columnType}`}>
            <BordOneToneIcon variant={variantIcon} customWidth="14px" stroke="#383838" />
          </div>
          <div className="trelloColumnHeaderContent">
            <div className="trelloColumnTitle">{title}</div>
            <div className="trelloColumnSubtitle">{subtitle}</div>
          </div>
        </div>
        {shouldShowTooltip ? (
          <BordTooltip position="top" message={itemsCount.toString()}>
            {counterContent}
          </BordTooltip>
        ) : (
          counterContent
        )}
      </div>
      <div ref={columnContentRef} className="trelloColumnContent" onScroll={handleScroll}>
        {isLoading
          ? Array.from({ length: 4 }).map((_, index) => <LoaderTrelloCard key={`loader-${index}`} />)
          : items && items?.length > 0 && renderItem
          ? items?.map((item, index) => (
              <React.Fragment key={`itemOrder-${index}-${item.orderId}`}>{renderItem(item)}</React.Fragment>
            ))
          : null}
        {isLoadingMoreItems && <LoaderTrelloCard key="loader-more" />}
      </div>
    </div>
  );
};

export default TrelloColumn;
